/**
 * 1.4.5: 成员卡片组件
 * 显示单个组织成员信息，支持角色变更和移除操作
 * 遵循 YUI 主题设计规范
 * 1.4.5: 修复菜单定位问题，确保删除成员选项可见
 */

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MoreVertical, RefreshCw, Trash2, Shield, User } from 'lucide-react';
import type { MemberRole } from '../services/organizationService';

interface MemberInfo {
  id: string;
  user_id: string;
  email: string;
  display_name?: string;
  role: MemberRole;
  status: string;
  joined_at: string;
}

interface MemberCardProps {
  member: MemberInfo;
  isOwner: boolean;
  isCurrentUser: boolean;
  onRoleChange?: (userId: string, newRole: MemberRole) => void;
  onRemove?: (userId: string, email: string) => void;
}

/**
 * 角色标签配色
 */
const getRoleBadgeStyle = (role: MemberRole): string => {
  switch (role) {
    case 'owner':
      return 'bg-purple-100 text-purple-700';
    case 'admin':
      return 'bg-blue-100 text-blue-700';
    case 'member':
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

/**
 * 角色显示名称
 */
const getRoleDisplayName = (role: MemberRole, t: (key: string, fallback?: string) => string): string => {
  switch (role) {
    case 'owner':
      return t('roleOwner', 'Owner');
    case 'admin':
      return t('roleAdmin', 'Admin');
    case 'member':
    default:
      return t('roleMember', 'Member');
  }
};

/**
 * 格式化加入日期
 */
const formatJoinDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

export function MemberCard({
  member,
  isOwner,
  isCurrentUser,
  onRoleChange,
  onRemove,
}: MemberCardProps) {
  const { t } = useTranslation();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // 点击外部关闭菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  // 获取用户首字母作为头像
  const getInitial = (): string => {
    if (member.display_name) {
      return member.display_name.charAt(0).toUpperCase();
    }
    return member.email.charAt(0).toUpperCase();
  };

  // 是否可以操作此成员（Owner 可以操作非自己的成员）
  const canManage = isOwner && !isCurrentUser;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-all">
      <div className="flex items-center justify-between">
        {/* 左侧：头像和信息 */}
        <div className="flex items-center gap-4">
          {/* 渐变头像 */}
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-purple-400 flex items-center justify-center text-white font-semibold text-lg">
            {getInitial()}
          </div>
          
          <div>
            {/* 显示名称或邮箱 */}
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">
                {member.display_name || member.email.split('@')[0]}
              </span>
              {isCurrentUser && (
                <span className="text-xs text-gray-400">
                  ({t('you', '你')})
                </span>
              )}
            </div>
            
            {/* 邮箱 */}
            <p className="text-sm text-gray-500">{member.email}</p>
            
            {/* 加入时间 */}
            <p className="text-xs text-gray-400 mt-1">
              {t('joinedAt', '加入于')} {formatJoinDate(member.joined_at)}
            </p>
          </div>
        </div>

        {/* 右侧：角色标签和操作 */}
        <div className="flex items-center gap-3">
          {/* 角色标签 */}
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRoleBadgeStyle(member.role)}`}>
            {getRoleDisplayName(member.role, t)}
          </span>
          
          {/* 操作菜单按钮 */}
          {canManage && (
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <MoreVertical className="w-5 h-5" />
              </button>
              
              {/* 1.4.5: 下拉菜单 - 修复定位和 z-index，确保完整显示 */}
              {showMenu && (
                <div className="absolute right-0 bottom-full mb-1 w-48 bg-white rounded-xl shadow-2xl border border-gray-200 py-2 z-50">
                  {/* 更改角色 */}
                  <button
                    onClick={() => {
                      const newRole = member.role === 'owner' ? 'member' : 'owner';
                      onRoleChange?.(member.user_id, newRole);
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                  >
                    {member.role === 'owner' ? (
                      <>
                        <User className="w-4 h-4 text-gray-500" />
                        {t('changeToMember', '设为普通成员')}
                      </>
                    ) : (
                      <>
                        <Shield className="w-4 h-4 text-primary" />
                        {t('changeToOwner', '设为管理员')}
                      </>
                    )}
                  </button>
                  
                  <div className="border-t border-gray-100 my-1 mx-2" />
                  
                  {/* 1.4.5: 移除成员选项 - 确保可见 */}
                  <button
                    onClick={() => {
                      onRemove?.(member.user_id, member.email);
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    {t('removeMember', '移除成员')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
